import { useCallback, useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { GatewayRenderer } from '../../../components/switches/gateway-renderer';
import type { PaymentMethodSetupErrorHandler } from '../types';
import {
  buildNoticeValue,
  noop,
  PaymentMethodSetupContext,
  PaymentMethodSetupProviderProps,
} from './payment-method-setup-base.context';
import { PaymentMethodSetupStripeProvider } from './payment-method-setup-stripe.context';
import { PaymentMethodSetupUnsupportedProvider } from './payment-method-setup-unsupported.context';

/** Renders the session's gateway. The session is created beforehand with `usePreparePaymentMethodSetup`. */
export const PaymentMethodSetupProvider = ({
  session,
  onError,
  onEvent = noop,
  onSavingChange,
  children,
  ...props
}: PaymentMethodSetupProviderProps) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();
  const { kind, onCancel } = props;

  const handleError = useCallback<PaymentMethodSetupErrorHandler>(
    (message, code) => (onError ? onError(message, code) : alerts.error(message)),
    [alerts, onError]
  );

  const missingSessionValue = useMemo(
    () =>
      buildNoticeValue({ kind, message: t("We couldn't start secure payment setup. Please try again."), onCancel, t }),
    [kind, onCancel, t]
  );

  if (!session) {
    return (
      <PaymentMethodSetupContext.Provider value={missingSessionValue}>{children}</PaymentMethodSetupContext.Provider>
    );
  }

  const gatewayProps = { ...props, session, onError: handleError, onEvent, onSavingChange };

  return (
    <GatewayRenderer
      activeGateway={session.gateway}
      stripeContent={PaymentMethodSetupStripeProvider}
      stripeProps={gatewayProps}
      justifiContent={PaymentMethodSetupUnsupportedProvider}
      justifiProps={gatewayProps}
    >
      {children}
    </GatewayRenderer>
  );
};
